'use client';

import { useState, useCallback, useRef } from 'react';
import { MasonryGrid } from './MasonryGrid';
import { AdSlot } from './AdSlot';
import toast from 'react-hot-toast';
import { useI18n } from '@/lib/i18n/context';
import { useFeedTranslate } from '@/lib/hooks/useTranslate';

interface PostItem {
  id: string; src: string; thumb?: string; title: string;
  tag?: string; author?: { name: string; username?: string; avatar?: string };
  views?: number; likes?: number; isLiked?: boolean; isSaved?: boolean;
}

interface HomeFeedProps {
  initialPosts: PostItem[];
  initialSort: string;
   endpoint?: string;
  initialCategory?: string;
}

export function HomeFeed({ initialPosts, initialSort, endpoint, initialCategory }: HomeFeedProps) {
  const [pages, setPages]         = useState<PostItem[][]>([initialPosts]);
  const [nextPage, setNextPage]   = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore]     = useState(initialPosts.length >= 20);
  const { t, locale } = useI18n();
  const fetchingRef = useRef(false);
  const urlCall = endpoint || '/api/posts';

  // Gom toàn bộ post đang hiển thị
  const allItems = pages.flat().map(p => ({ id: p.id, title: p.title }));
  const { titleMap } = useFeedTranslate(allItems, locale);

  const loadMore = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);

    try {
      setNextPage(prev => {
        const currentPage = prev;
        (async () => {
          try {
            const params = new URLSearchParams({
              page: currentPage.toString(),
              sort: initialSort,
              ...(initialCategory ? { category: initialCategory } : {}),
            });
            const res = await fetch(`${urlCall}?${params}`);
            if (!res.ok) throw new Error('fetch failed');
            const data: PostItem[] = await res.json();
            if (data.length < 20) setHasMore(false);
            if (data.length > 0) {
              setPages(prev => {
                const existingIds = new Set(prev.flat().map(x => x.id));
                const fresh = data.filter(x => !existingIds.has(x.id));
                if (!fresh.length) return prev;
                return [...prev, fresh];
              });
            }
          } catch {
            toast.error(t.common.error);
          } finally {
            setIsLoading(false);
            fetchingRef.current = false;
          }
        })();
        return currentPage + 1;
      });
    } catch {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [initialSort, initialCategory, t]);

  return (
    <div className="py-4">
      {pages.map((pagePosts, index) => {
        const isLast = index === pages.length - 1;
        return (
          <div key={index}>
            <MasonryGrid
              // posts={pagePosts}
              posts={pagePosts.map(p => ({
                ...p,
                title: titleMap.get(p.id) ?? p.title,
              }))}
              isLoading={isLast ? isLoading : false}
              hasMore={isLast ? hasMore : false}
              onLoadMore={isLast ? loadMore : undefined}
              skeletonCount={10}
              isLastPage={isLast}
            />

            {/*
              ③ 300×100 giữa các page block (sau mỗi ~20 pins đã load xong,
              trừ page cuối — page cuối đã có ad trong MasonryGrid)
            */}
            {!isLast && (
              <div className="flex flex-col items-center gap-1 my-4">
                <AdSlot type="728x90-2" />
                <p className="text-gray-600 text-[10px]">Quảng cáo</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}